import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';

type Salon = {
  id: string; name: string | null; state_code: string | null;
  license_number: string | null; timezone: string | null; created_at: string;
};

type Stylist = {
  id: string; user_id: string; name: string | null;
  role: string; is_active: boolean | null; created_at: string;
};

type ProgressRow = {
  stylist: Stylist;
  hoursLogged: number | null;
  hoursApproved: number | null;
  modulesAssigned: number | null;
  lessonsCompleted: number | null;
};

export function StudioDetail() {
  const { salonId } = useParams<{ salonId: string }>();
  const [salon, setSalon] = useState<Salon | null>(null);
  const [stylists, setStylists] = useState<Stylist[] | null>(null);
  const [progress, setProgress] = useState<Record<string, ProgressRow> | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!salonId) return;
    (async () => {
      const [salonRes, styRes] = await Promise.all([
        supabase.from('salons').select('*').eq('id', salonId).maybeSingle(),
        supabase.from('stylists').select('id,user_id,name,role,is_active,created_at')
          .eq('salon_id', salonId).order('role').order('name'),
      ]);
      if (salonRes.error) setErr(salonRes.error.message);
      setSalon((salonRes.data as Salon | null) ?? null);
      const sty = (styRes.data ?? []) as Stylist[];
      setStylists(sty);

      // Per-apprentice progress
      const apprentices = sty.filter((s) => s.role === 'apprentice');
      const rows: Record<string, ProgressRow> = {};
      await Promise.all(apprentices.map(async (s) => {
        const [hoursAll, hoursApp, assigned, lessons] = await Promise.all([
          supabase.from('hour_logs').select('id', { count: 'exact', head: true }).eq('apprentice_id', s.id),
          supabase.from('hour_logs').select('id', { count: 'exact', head: true })
            .eq('apprentice_id', s.id).eq('approval_status', 'approved'),
          supabase.from('module_assignments').select('id', { count: 'exact', head: true })
            .eq('assigned_to', s.id),
          supabase.from('lesson_progress').select('id', { count: 'exact', head: true })
            .eq('user_id', s.user_id).eq('status', 'completed'),
        ]);
        rows[s.id] = {
          stylist: s,
          hoursLogged: hoursAll.count ?? null,
          hoursApproved: hoursApp.count ?? null,
          modulesAssigned: assigned.count ?? null,
          lessonsCompleted: lessons.count ?? null,
        };
      }));
      setProgress(rows);
    })();
  }, [salonId]);

  if (err) return <div className="panel">Error: {err}</div>;
  if (!salon) return <div className="muted">Loading…</div>;

  const stylistStaff = stylists?.filter((s) => s.role !== 'apprentice') ?? [];
  const apprentices = stylists?.filter((s) => s.role === 'apprentice') ?? [];

  return (
    <>
      <div className="page-head">
        <div>
          <div className="page-eyebrow"><Link to="/studios">Studios</Link> · {salon.state_code ?? '—'}</div>
          <h1 className="page-title">{salon.name ?? '(unnamed studio)'}</h1>
          <p className="page-sub">
            License <code>{salon.license_number ?? '—'}</code> · {salon.timezone ?? 'no tz'} ·
            since {new Date(salon.created_at).toLocaleDateString()}
          </p>
        </div>
      </div>

      <div className="kpi-grid">
        <Kpi label="Stylists" value={stylistStaff.length} tone="moss" />
        <Kpi label="Apprentices" value={apprentices.length} tone="dusty" />
        <Kpi label="Total team" value={stylists?.length ?? 0} tone="sage" />
      </div>

      <div className="panel-grid">
        <section className="panel">
          <div className="panel-head"><span className="panel-title">Stylists ({stylistStaff.length})</span></div>
          {stylistStaff.length === 0 && <p className="muted">No stylists yet.</p>}
          {stylistStaff.map((s) => (
            <div key={s.id} className="listrow">
              <span><strong>{s.name ?? '(unnamed)'}</strong> <span className="muted">· {s.role}</span></span>
              <span className={`chip ${s.is_active ? 'chip--ok' : 'chip--warn'}`}>
                {s.is_active ? 'active' : 'inactive'}
              </span>
            </div>
          ))}
        </section>

        <section className="panel">
          <div className="panel-head"><span className="panel-title">Apprentices ({apprentices.length})</span></div>
          {apprentices.length === 0 && <p className="muted">No apprentices yet.</p>}
          {apprentices.map((s) => {
            const p = progress?.[s.id];
            return (
              <div key={s.id} style={{ padding: '12px 0', borderTop: '1px solid var(--rule)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <strong>{s.name ?? '(unnamed)'}</strong>
                  <span className="muted" style={{ fontFamily: 'var(--mono)', fontSize: 11 }}>
                    {p?.hoursApproved ?? '—'} / {p?.hoursLogged ?? '—'} hrs · {p?.lessonsCompleted ?? '—'} lessons
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <span className="chip chip--info">{p?.modulesAssigned ?? 0} modules</span>
                  <span className="chip">{p?.lessonsCompleted ?? 0} done</span>
                </div>
              </div>
            );
          })}
        </section>
      </div>
    </>
  );
}

function Kpi({ label, value, tone }: { label: string; value: number; tone: 'moss' | 'clay' | 'dusty' | 'sage' }) {
  return (
    <div className={`kpi kpi--${tone}`}>
      <div className="kpi-top"><div className="kpi-label">{label}</div></div>
      <div className="kpi-num">{value}</div>
    </div>
  );
}
